<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import {
  Building2,
  ExternalLink,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  X,
} from "lucide-vue-next";
import AppShell from "@/components/workspace/AppShell.vue";
import { useWorkspaceStore } from "@/stores/workspace";
import type { Organization } from "@/types/models";

const workspace = useWorkspaceStore();
withDefaults(defineProps<{ embedded?: boolean }>(), { embedded: false });
const query = ref("");
const formOpen = ref(false);
const editing = ref<Organization | null>(null);
const saving = ref(false);
const loadingOrganizations = ref(true);
const error = ref("");
const form = reactive({
  name: "",
  website: "",
  logoUrl: "",
  notes: "",
});

const visible = computed(() => {
  const needle = query.value.trim().toLowerCase();
  return workspace.organizations.filter((organization) =>
    `${organization.name} ${organization.website} ${organization.logoUrl} ${organization.notes}`
      .toLowerCase()
      .includes(needle),
  );
});

onMounted(() => {
  void workspace
    .loadOrganizations()
    .catch(() => {
      error.value =
        "Organizations could not be loaded. Check your connection and try again.";
    })
    .finally(() => {
      loadingOrganizations.value = false;
    });
});

function openCreate() {
  editing.value = null;
  Object.assign(form, { name: "", website: "", logoUrl: "", notes: "" });
  error.value = "";
  formOpen.value = true;
}

function openEdit(organization: Organization) {
  editing.value = organization;
  Object.assign(form, {
    name: organization.name,
    website: organization.website,
    logoUrl: organization.logoUrl,
    notes: organization.notes,
  });
  error.value = "";
  formOpen.value = true;
}

async function saveOrganization() {
  if (!form.name.trim() || saving.value) return;
  saving.value = true;
  error.value = "";
  try {
    if (editing.value)
      await workspace.updateOrganization(editing.value.id, {
        name: form.name.trim(),
        website: form.website.trim(),
        logoUrl: form.logoUrl.trim(),
        notes: form.notes.trim(),
        version: editing.value.version,
      });
    else
      await workspace.createOrganization({
        name: form.name.trim(),
        website: form.website.trim(),
        logoUrl: form.logoUrl.trim(),
        notes: form.notes.trim(),
      });
    Object.assign(form, {
      name: "",
      website: "",
      logoUrl: "",
      notes: "",
    });
    formOpen.value = false;
    editing.value = null;
  } catch (saveError) {
    error.value =
      saveError instanceof Error
        ? saveError.message
        : "The organization could not be saved. Your changes are still in the form.";
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <component :is="embedded ? 'div' : AppShell">
    <main
      class="directory-page"
      :class="{ 'directory-page--embedded': embedded }"
    >
      <header class="directory-page__header">
        <div>
          <p class="eyebrow">Workspace directory</p>
          <h1>Organizations</h1>
          <p>
            Keep reusable client details in one place. Project access always
            remains an explicit invitation.
          </p>
        </div>
        <button
          v-if="workspace.user?.canCreateProjects"
          class="button button--primary"
          type="button"
          @click="openCreate"
        >
          <Plus :size="17" /> Add organization
        </button>
      </header>

      <p v-if="error" class="page-alert" role="alert">{{ error }}</p>

      <section class="directory-sheet">
        <header>
          <label class="directory-search">
            <Search :size="16" aria-hidden="true" />
            <span class="sr-only">Search organizations</span>
            <input v-model="query" placeholder="Search organizations" />
          </label>
          <span
            >{{ visible.length }} organization{{
              visible.length === 1 ? "" : "s"
            }}</span
          >
        </header>
        <div class="directory-table-head" aria-hidden="true">
          <span>Organization</span><span>Website</span><span>Notes</span
          ><span>Status</span><span></span>
        </div>
        <article
          v-for="organization in visible"
          :key="organization.id"
          class="organization-row"
        >
          <span class="organization-name">
            <span class="organization-mark">
              <img
                v-if="organization.logoDataUrl"
                :src="organization.logoDataUrl"
                :alt="`${organization.name} logo`"
              />
              <template v-else>{{
                organization.name.slice(0, 2).toUpperCase()
              }}</template>
            </span>
            <strong>{{ organization.name }}</strong>
          </span>
          <a
            v-if="organization.website"
            :href="organization.website"
            target="_blank"
            rel="noreferrer"
          >
            {{ organization.website.replace(/^https?:\/\//, "") }}
            <ExternalLink :size="13" />
          </a>
          <span v-else class="muted">Not added</span>
          <span class="organization-context">
            <small>{{ organization.notes || "No notes" }}</small>
          </span>
          <span class="status-label"><ShieldCheck :size="15" />Active</span>
          <button
            v-if="workspace.user?.canCreateProjects"
            class="button button--secondary organization-edit"
            type="button"
            :aria-label="`Edit ${organization.name}`"
            @click="openEdit(organization)"
          >
            <Pencil :size="15" /> Edit
          </button>
        </article>
        <div v-if="loadingOrganizations" class="directory-empty" role="status">
          <Building2 :size="28" />
          <strong>Loading organizations…</strong>
          <p>Client details will appear here.</p>
        </div>
        <div v-else-if="!visible.length" class="directory-empty">
          <Building2 :size="28" />
          <strong>No organizations match this search.</strong>
          <p>Try another name or clear the search.</p>
        </div>
      </section>

      <div v-if="formOpen" class="modal-layer">
        <button
          class="modal-scrim"
          type="button"
          aria-label="Close organization form"
          @click="formOpen = false"
        ></button>
        <form
          class="modal-card"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="
            editing ? 'edit-organization-title' : 'add-organization-title'
          "
          @submit.prevent="saveOrganization"
        >
          <header>
            <div>
              <h2
                :id="
                  editing ? 'edit-organization-title' : 'add-organization-title'
                "
              >
                {{ editing ? "Edit organization" : "Add organization" }}
              </h2>
              <p>
                Organization details improve suggestions. They do not change
                project access.
              </p>
            </div>
            <button
              class="icon-button"
              type="button"
              aria-label="Close"
              @click="formOpen = false"
            >
              <X :size="19" />
            </button>
          </header>
          <p v-if="error" class="modal-error" role="alert">{{ error }}</p>
          <div class="field">
            <label for="organization-name">Organization name</label>
            <input
              id="organization-name"
              v-model="form.name"
              class="input"
              required
              maxlength="120"
              autofocus
            />
          </div>
          <div class="field">
            <label for="organization-website">Website</label>
            <input
              id="organization-website"
              v-model="form.website"
              class="input"
              type="url"
              placeholder="https://example.com"
            />
          </div>
          <div class="field">
            <label for="organization-logo">Logo image URL</label>
            <input
              id="organization-logo"
              v-model="form.logoUrl"
              class="input"
              type="url"
              placeholder="https://example.com/logo.svg"
            />
            <p class="helper">
              Use a direct public HTTPS link to an SVG, PNG, or JPEG under 256
              KB. SVGs may contain paths, groups, rectangles, and simple
              translations. The image is sanitized and stored so participants do
              not load it from the source website.
            </p>
          </div>
          <div class="field">
            <label for="organization-notes">Notes</label>
            <textarea
              id="organization-notes"
              v-model="form.notes"
              class="textarea"
              maxlength="2000"
              placeholder="Relationship context or naming notes"
            ></textarea>
          </div>
          <footer>
            <button
              class="button button--secondary"
              type="button"
              @click="formOpen = false"
            >
              Cancel
            </button>
            <button
              class="button button--primary"
              :disabled="!form.name.trim() || saving"
              type="submit"
            >
              {{
                saving
                  ? "Saving…"
                  : editing
                    ? "Save organization"
                    : "Add organization"
              }}
            </button>
          </footer>
        </form>
      </div>
    </main>
  </component>
</template>

<style scoped>
.directory-page {
  min-height: 100%;
  padding: 30px clamp(18px, 4vw, 54px) 60px;
}
.directory-page--embedded {
  padding: 0;
}
.directory-page--embedded .directory-page__header {
  max-width: none;
}
.directory-page--embedded .directory-sheet,
.directory-page--embedded .page-alert {
  max-width: none;
}
.directory-page__header {
  max-width: 1180px;
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 24px;
  margin: 0 auto 22px;
}
.directory-page__header h1 {
  margin: 2px 0 6px;
  color: var(--ink-900);
  font-size: 30px;
}
.directory-page__header p:not(.eyebrow) {
  max-width: 680px;
  color: var(--text-soft);
}
.eyebrow {
  color: var(--brand-strong);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.page-alert {
  max-width: 1180px;
  margin: 0 auto 14px;
  padding: 11px 13px;
  border: 1px solid var(--danger);
  border-radius: 5px;
  background: var(--danger-soft);
  color: var(--danger);
}
.directory-sheet {
  container: directory-sheet / inline-size;
  max-width: 1180px;
  margin: auto;
  overflow: hidden;
  border: 1px solid var(--rule);
  border-radius: 8px;
  background: white;
}
.directory-sheet > header {
  min-height: 58px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--rule);
  color: var(--text-soft);
  font-size: 12px;
}
.directory-search {
  width: min(420px, 100%);
  min-height: 36px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  border: 1px solid var(--rule);
  border-radius: 5px;
}
.directory-search input {
  width: 100%;
  border: 0;
  outline: 0;
}
.directory-table-head,
.organization-row {
  display: grid;
  grid-template-columns:
    minmax(220px, 1.2fr) minmax(170px, 1fr) 1.5fr 100px
    auto;
  gap: 16px;
  align-items: center;
}
.directory-table-head {
  padding: 8px 16px;
  background: var(--sheet-subtle);
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.organization-row {
  min-height: 68px;
  padding: 10px 16px;
  border-top: 1px solid var(--rule);
}
.organization-row a {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--shared);
}
.organization-row > span {
  min-width: 0;
  overflow-wrap: anywhere;
}
.organization-context {
  display: grid;
  gap: 5px;
}
.organization-context small {
  color: var(--text-soft);
}
.organization-name {
  display: flex;
  align-items: center;
  gap: 10px;
}
.organization-mark {
  width: 34px;
  height: 34px;
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  overflow: hidden;
  border: 1px solid var(--rule-strong);
  border-radius: 5px;
  background: var(--sheet-subtle);
  color: var(--ink-800);
  font-size: 11px;
  font-weight: 800;
}
.organization-mark img {
  width: 100%;
  height: 100%;
  padding: 3px;
  object-fit: contain;
}
.status-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--success);
  font-size: 12px;
  font-weight: 700;
}
.organization-edit {
  min-width: max-content;
}
.directory-empty {
  min-height: 220px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 7px;
  color: var(--text-soft);
  text-align: center;
}
.modal-layer {
  position: fixed;
  z-index: 100;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 16px;
}
.modal-scrim {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(13, 35, 50, 0.48);
}
.modal-card {
  position: relative;
  width: min(560px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
  display: grid;
  gap: 18px;
  padding: 22px;
  border-radius: 8px;
  background: white;
  box-shadow: var(--shadow-panel);
}
.modal-card > header,
.modal-card > footer {
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 16px;
}
.modal-card > header p {
  margin-top: 4px;
  color: var(--text-soft);
}
.modal-error {
  padding: 10px 12px;
  border: 1px solid var(--danger);
  border-radius: 5px;
  background: var(--danger-soft);
  color: var(--danger);
}
.modal-card > footer {
  justify-content: flex-end;
  padding-top: 4px;
}
@media (max-width: 760px) {
  .directory-page__header {
    align-items: stretch;
    flex-direction: column;
  }
}
@container directory-sheet (max-width: 760px) {
  .directory-table-head {
    display: none;
  }
  .organization-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  .organization-edit {
    justify-content: center;
  }
}
</style>
